/**
 * Shared validation primitives for untrusted daemon rows.
 *
 * Every list the PWA renders comes from the daemon (or localStorage written
 * from a daemon response), so ids, numbers and capped strings are checked here
 * before they reach state or the UI. These helpers are pure and browser-safe.
 */

export const ENTITY_ID_MAX_CHARS = 128;

/** Ids are opaque daemon identifiers: uuid/base64url-ish, never HTML. */
const ENTITY_ID_PATTERN = /^[A-Za-z0-9_.:-]{1,128}$/u;

/** Timestamps above this are epoch milliseconds rather than epoch seconds. */
const MS_THRESHOLD = 1e11;

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function isEntityId(value: unknown): value is string {
  return typeof value === 'string' && ENTITY_ID_PATTERN.test(value);
}

/**
 * Coerce a daemon timestamp to epoch seconds.
 *
 * The daemon HTTP API serialises timestamps as ISO 8601 strings (UTC `Z`);
 * SQLite/file rows elsewhere use epoch milliseconds. Both are normalized at
 * this boundary, and missing/invalid values become 0.
 */
export function normalizedTimestamp(value: unknown): number {
  if (typeof value === 'string') return isoStringToSeconds(value) ?? 0;
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) return 0;
  return Math.floor(value > MS_THRESHOLD ? value / 1000 : value);
}

/** Optional timestamp: `null`/`undefined`/`0`/invalid mean "unset". */
export function optionalTimestamp(value: unknown): number | null {
  if (value === null || value === undefined || value === 0) return null;
  if (typeof value === 'string') return isoStringToSeconds(value);
  const seconds = normalizedTimestamp(value);
  return seconds > 0 ? seconds : null;
}

/** ISO 8601 (with offset) → epoch seconds, or null when unparseable. */
export function isoStringToSeconds(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;
  const milliseconds = Date.parse(trimmed);
  if (!Number.isFinite(milliseconds) || milliseconds <= 0) return null;
  return Math.floor(milliseconds / 1000);
}

/**
 * Trim + cap user/daemon text by Unicode code points (so emoji are not split).
 * The caller decides whether empty text is valid.
 */
export function cappedText(value: string, maxChars: number): string {
  const chars = Array.from(value.trim());
  return chars.length > maxChars ? chars.slice(0, maxChars).join('') : chars.join('');
}

/** Same cap but preserves the caller's whitespace (notes keep layout). */
export function cappedRawText(value: string, maxChars: number): string {
  const chars = Array.from(value);
  return chars.length > maxChars ? chars.slice(0, maxChars).join('') : chars.join('');
}

export function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}
